import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import GuideInfo from '@/components/app/guide/GuideInfo';
import ShareHandoff from '@/components/app/shareFreeContent/ShareHandoff';
import SharePayment from '@/components/app/shareFreeContent/SharePayment';
import SharePrimer from '@/components/app/shareFreeContent/SharePrimer';
import { useShareContext } from '@/components/app/shareFreeContent/ShareProvider';
import Loader from '@/components/Loader';
import StripeElementsProvider from '@/components/purchase/StripeElementsProvider';
import UpsellBanner from '@/components/UpsellBanner';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useThemeState, useUserState } from '@/hooks/store';
import { useGetIsTeamsApp } from '@/utils/app/msftTeams';
import { userCanTrial } from '@/utils/subscriptions';
import chevronLeft from 'icons/chevron-left.svg';

import messages from './messages';
import { BackButton, Grid, Wrapper } from './styles';
import { GuideLayoutProps } from './types';

const GuideLayout = ({ guide, program, rotateBackButton }: GuideLayoutProps) => {
	const router = useRouter();
	const user = useUserState();
	const theme = useThemeState();
	const { logEvent } = useAnalytics();
	const { onStop, hideControls, shouldPreventAppNav, playerMode } = useMediaPlayerContext();
	const { shareToken, shareSenderFirstName, isFetchingShare } = useShareContext();
	const { formatMessage } = useIntl();
	const { isTeamsApp } = useGetIsTeamsApp();
	const { setIsModalOpen } = useSimplifiedSignupModalContext();

	const [isVisible, setIsVisible] = useState(true);
	const [isRouting, setIsRouting] = useState(false);
	const [startedContent, setStartedContent] = useState(false);

	const isPreviewMode = !guide?.is_free && !user?.subscription?.valid;

	const shouldHideBackButton = shouldPreventAppNav && !program;

	const onBackClick = async () => {
		logEvent({ eventName: 'Audio Session : Back : Clicked' });
		const isExternalApp = theme.isZoomApp || isTeamsApp;
		if (isExternalApp) {
			await onStop();
		} else {
			router.back();
		}
	};

	useEffect(() => {
		logEvent({ eventName: 'Player Program : Landed' });
		return setIsVisible(false);
	}, [shareSenderFirstName, shareToken, program, guide, logEvent]);

	useEffect(() => {
		logEvent({
			eventName: 'Screen : Viewed',
			eventProps: {
				screen: 'Signup Primer Screen',
				source: 'Shared Link',
				user_eligible_for_30D_trial: userCanTrial(user),
				share_token: shareToken as string,
				sender_first_name: shareSenderFirstName ? (shareSenderFirstName as string) : null,
				author_name: program?.author?.name || null,
				program_title: program?.title || null,
				program_id: program?.id || null,
				meditation_type: program?.meditation_type || null,
				narrator_name: program?.narrator?.name || null,
				program_language: program?.language || null,
				program_is_free: guide?.is_free || false,
				narrator_id: program?.narrator?.id || null,
			},
		});
	}, [shareSenderFirstName, shareToken, program, guide, logEvent, user]);

	useEffect(() => {
		const enableLoader = () => setIsRouting(true);
		const disableLoader = () => setIsRouting(false);

		router.events.on('routeChangeStart', enableLoader);
		router.events.on('routeChangeComplete', disableLoader);

		return () => {
			router.events.off('routeChangeStart', enableLoader);
			router.events.off('routeChangeComplete', disableLoader);
		};
	}, [router.events]);

	useEffect(() => {
		if (playerMode === 'stopped' && isPreviewMode && startedContent) {
			setIsModalOpen(true);
		}
		if (playerMode === 'playing' && !startedContent) {
			setStartedContent(true);
		}
	}, [onStop, isPreviewMode, playerMode, setIsModalOpen, startedContent]);

	const showBackButton = !shouldHideBackButton && !hideControls && !shareToken && !isPreviewMode;

	return (
		<StripeElementsProvider isStripeNext>
			<UpsellBanner />
			<Grid columns={4}>
				<Wrapper isVisible={isVisible}>
					<SharePrimer isPreviewMode={isPreviewMode} />

					<SharePayment />

					<ShareHandoff />

					{showBackButton && (
						<BackButton
							aria-label={formatMessage(messages.backButtonLabel)}
							color="white"
							onPress={onBackClick}
							$rotate={rotateBackButton}
							Icon={chevronLeft}
							data-testid="back-button"
						/>
					)}
					{isRouting || isFetchingShare ? <Loader /> : <GuideInfo program={program} guide={guide} />}
				</Wrapper>
			</Grid>
		</StripeElementsProvider>
	);
};

export default GuideLayout;
